// Link kinds step 2 — a chat message's link plays in the stream dock (Figma Shared / Dock / Chat link card 4358:3223).
// One at a time, and never beside the wall card: wallPlaybackStore.play stops the chat link, and the wall store stops
// itself when a chat link starts (its subscription below this store, so chat screens need not load the wall store).
// Android: one chat player, `WallPlayback.playChatLink`.
import { create, type StoreApi, type UseBoundStore } from "zustand";
import type { ChatLink } from "../types/chat";
import { useAuthStore } from "./authStore";

/** What the dock plays and where it came from (Android `ChatLinkNowPlaying`). */
export interface ChatLinkNowPlaying {
  link: ChatLink;
  /** Shown as "From chat · <sender>" ("You" for your own message). */
  senderName: string | null;
  convId: string | null;
  /** The conversation's type (GROUP, PAIR, …): where Go to message opens it. */
  convType: string | null;
  convName: string | null;
  /** The stored message id (a live row's synthetic id is not one) and the sender's local id. */
  messageId: string | null;
  localId: string | null;
  /** The message's time_stamp (ms). */
  timeStamp: number | null;
}

/** The same message's link: same conversation and url, matched by message id or local id. */
export function sameChatLink(a: ChatLinkNowPlaying, b: ChatLinkNowPlaying): boolean {
  if (a.convId !== b.convId || a.link.url !== b.link.url) return false;
  return (a.localId != null && a.localId === b.localId) || (a.messageId != null && a.messageId === b.messageId);
}

export interface ChatLinkPlayback {
  np: ChatLinkNowPlaying;
  paused: boolean;
  failed: boolean;
}

/** Go to message: a pending "show this message" request the conversation body picks up. */
export interface ChatLinkJump {
  convId: string;
  messageId: string | null;
  localId: string | null;
  timeStamp: number;
  nonce: number;
}

export interface ChatLinkPlaybackState {
  current: ChatLinkPlayback | null;
  jump: ChatLinkJump | null;
  /** Plays this link in the dock (replacing any chat link); wall playback stops. */
  play(np: ChatLinkNowPlaying): void;
  togglePause(): void;
  /** The link finished: paused (one link, no next). */
  ended(): void;
  failed(): void;
  stop(): void;
  /** The message was deleted (Android `WallPlayback.stopIfChatMessage`): stops when it plays that message's link. */
  stopIfMessage(convId: string, messageId: string | null, localId: string | null): void;
  requestJump(np: ChatLinkNowPlaying): void;
  /** The body took the jump. */
  clearJump(nonce: number): void;
  reset(): void;
}

let jumpSeq = 0;

export const useChatLinkPlaybackStore: UseBoundStore<StoreApi<ChatLinkPlaybackState>> = create<ChatLinkPlaybackState>()(
  (set, get) => {
    const patch = (p: Partial<ChatLinkPlayback>) => {
      const cur = get().current;
      if (cur) set({ current: { ...cur, ...p } });
    };
    return {
      current: null,
      jump: null,
      play(np) {
        set({ current: { np, paused: false, failed: false } });
      },
      togglePause() {
        const cur = get().current;
        if (cur) patch({ paused: !cur.paused, failed: false });
      },
      ended() {
        patch({ paused: true });
      },
      failed() {
        patch({ failed: true, paused: true });
      },
      stop() {
        if (get().current) set({ current: null });
      },
      stopIfMessage(convId, messageId, localId) {
        const np = get().current?.np;
        if (!np || np.convId !== convId) return;
        if ((messageId != null && np.messageId === messageId) || (localId != null && np.localId === localId))
          set({ current: null });
      },
      requestJump(np) {
        if (np.convId == null || np.timeStamp == null) return;
        set({ jump: { convId: np.convId, messageId: np.messageId, localId: np.localId, timeStamp: np.timeStamp, nonce: ++jumpSeq } });
      },
      clearJump(nonce) {
        if (get().jump?.nonce === nonce) set({ jump: null });
      },
      reset() {
        set({ current: null, jump: null });
      },
    };
  },
);

// Logout or another account: the next user never inherits this playback. (`?.`: some tests mock the auth store bare.)
useAuthStore.subscribe?.((s, prev) => {
  if ((prev.username && !s.username) || (prev.userId != null && s.userId !== prev.userId)) useChatLinkPlaybackStore.getState().reset();
});
